/**
 * Hello Retail Search Results Renderer
 * Handles rendering of search results with filters, sorting, and infinite scroll
 */

(function () {
    'use strict';

    const HR_RECOMS_ENDPOINT = 'https://core.helloretail.com/serve/recoms';

    /** @type {Promise<string|null>|null} */
    let helloRetailTrackingUserIdPromise = null;

    /**
     * Hello Retail tracking id for REST recoms (see https://developer.helloretail.com/sdk/tracking/tracking_user/).
     * Single-flight: concurrent callers share one SDK callback.
     * @param {number} [timeoutMs]
     * @returns {Promise<string|null>}
     */
    function getHelloRetailTrackingUserId(timeoutMs = 5000) {
        if (helloRetailTrackingUserIdPromise) {
            return helloRetailTrackingUserIdPromise;
        }
        helloRetailTrackingUserIdPromise = new Promise((resolve) => {
            let settled = false;
            const finish = (id) => {
                if (settled) return;
                settled = true;
                const normalized =
                    id != null && typeof id === 'string' && id.trim() ? id.trim() : null;
                resolve(normalized);
            };
            const timer = setTimeout(() => finish(null), timeoutMs);
            try {
                window.hrq = window.hrq || [];
                window.hrq.push([
                    'getTrackingUserId',
                    function (trackingUserId) {
                        clearTimeout(timer);
                        finish(trackingUserId);
                    }
                ]);
            } catch (e) {
                clearTimeout(timer);
                finish(null);
            }
        });
        return helloRetailTrackingUserIdPromise;
    }

    function getHelloRetailDeviceType() {
        try {
            const mq = window.themeVariables?.mediaQueries?.sm;
            if (mq && typeof window.matchMedia === 'function') {
                return window.matchMedia(mq).matches ? 'DESKTOP' : 'MOBILE';
            }
            return window.matchMedia('(max-width: 699px)').matches ? 'MOBILE' : 'DESKTOP';
        } catch (e) {
            return 'DESKTOP';
        }
    }

    /**
     * @param {string} websiteUuid
     * @param {object[]} requests
     * @param {string|null} trackingUserId
     */
    function buildRecomsRequestBody(websiteUuid, requests, trackingUserId) {
        const body = { websiteUuid, requests };
        if (trackingUserId) {
            body.trackingUserId = trackingUserId;
        }
        return body;
    }

    /**
     * Hello Retail expects `context.productNumbers` to be a list, not a comma-separated string.
     * @param {unknown} value
     * @returns {string[]|null}
     */
    function normalizeProductNumbersToList(value) {
        if (value == null) {
            return null;
        }
        if (Array.isArray(value)) {
            const out = value.map((x) => String(x).trim()).filter(Boolean);
            return out.length ? out : null;
        }
        const s = typeof value === 'string' ? value.trim() : String(value).trim();
        if (!s) {
            return null;
        }
        const out = s.split(',').map((x) => x.trim()).filter(Boolean);
        return out.length ? out : null;
    }

    /**
     * Managed recom request (https://developer.helloretail.com/api/recoms/).
     * @param {{ key: string, format?: string, context?: object, productNumbers?: string }} options
     */
    function buildManagedRecomRequestItem(options) {
        const { key, format = 'json', context: inputContext = {}, productNumbers } = options;
        const item = { key, format };
        const context = Object.assign({}, inputContext);

        const fromContext = normalizeProductNumbersToList(context.productNumbers);
        const fromArg = normalizeProductNumbersToList(productNumbers);
        const mergedList = fromContext && fromContext.length ? fromContext : fromArg;

        if (mergedList && mergedList.length) {
            context.productNumbers = mergedList;
        } else {
            delete context.productNumbers;
        }

        if (Object.keys(context).length > 0) {
            item.context = context;
        }
        /*
        if (mergedList && mergedList.length) {
            item.productNumbers = mergedList.join(',');
        }
        */
        item.deviceType = getHelloRetailDeviceType();
        return item;
    }

    /**
     * Read optional JSON context from data-hr-context, or from split attributes on .hr-recom-box.
     * Hello Retail Managed boxes use `requests[].context` (see https://developer.helloretail.com/api/recoms/).
     * Typical keys: `urls` (canonical collection/product URLs in the HR index), `productNumbers` (list),
     * `hierarchies` (category paths from your feed — confirm shape in My Hello Retail / Postman Managed),
     * `extraDataList` (e.g. `{ collectionIds: [id] }` for HR filters referencing `$extraDataList.collectionIds`).
     * A hidden input like `#hrCollectionId` is not read; put collection URL on `data-hr-context-urls` or in
     * `data-hr-context` JSON instead.
     * @param {Element} element
     * @returns {Record<string, unknown>}
     */
    function parseHrRecomContextFromElement(element) {
        const ctx = {};
        const rawJson = element.getAttribute('data-hr-context');
        if (rawJson) {
            try {
                const parsed = JSON.parse(rawJson);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                    Object.assign(ctx, parsed);
                }
            } catch (e) {
                /* ignore invalid JSON */
            }
        }
        const pn = (element.getAttribute('data-hr-context-product-numbers') || '').trim();
        if (pn && !ctx.productNumbers) {
            ctx.productNumbers = pn;
        }
        const urlsBlob = element.getAttribute('data-hr-context-urls');
        if (urlsBlob && !ctx.urls) {
            const urls = urlsBlob
                .split('|')
                .map((s) => s.trim())
                .filter(Boolean);
            if (urls.length) {
                ctx.urls = urls;
            }
        }
        const singleUrl = (element.getAttribute('data-hr-product-url') || '').trim();
        if (singleUrl && !ctx.urls) {
            ctx.urls = [singleUrl];
        }
        const legacyPid = (element.getAttribute('data-product-id') || '').trim();
        if (legacyPid && !ctx.productNumbers) {
            ctx.productNumbers = legacyPid;
        }
        return ctx;
    }

    /**
     * @param {string} websiteUuid
     * @param {object[]} requests
     * @param {AbortSignal|null} [signal]
     */
    async function postHelloRetailRecoms(websiteUuid, requests, signal = null) {
        const trackingUserId = await getHelloRetailTrackingUserId();
        if (signal?.aborted) {
            throw new DOMException('Aborted', 'AbortError');
        }
        const body = buildRecomsRequestBody(websiteUuid, requests, trackingUserId);
        const response = await fetch(HR_RECOMS_ENDPOINT, {
            method: 'POST',
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body),
            signal: signal || undefined
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    }

    class HelloRetailSearchRenderer {
        constructor(container) {
            this.container = container;
            this.currentData = null;
            this.currentPage = 1;
            this.productCount = 20;
            this.isLoading = false;
            this.hasMore = true;
            this.activeFilters = {};
            this.currentSort = '';
            this.searchTerm = '';
            this.searchConfigKey = window.themeVariables?.settings?.helloRetailSearchConfigKey || '';
            this.websiteUuid = window.themeVariables?.settings?.helloRetailWebsiteUuid || '';
            this.initialContentProductBoxKey = window.themeVariables?.settings?.helloRetailInitialContentProductBoxKey || '';
            this.abortController = null;
            this.lastRenderSearchTerm = null;
            this.sizeSelectorListenersInitialized = false;
        }

        /**
         * Render search results - fetch data and render
         */
        async render(searchTerm) {
            if (!searchTerm) return;

            // Prevent duplicate renders with same searchTerm
            if (this.lastRenderSearchTerm === searchTerm && this.isLoading) {
                console.log('HelloRetailSearchRenderer: Skipping duplicate render for:', searchTerm);
                return;
            }

            // Abort previous request if still loading
            if (this.abortController) {
                this.abortController.abort();
            }

            this.searchTerm = searchTerm;
            this.lastRenderSearchTerm = searchTerm;

            // Reset pagination and filters when new search
            this.currentPage = 1;
            this.activeFilters = {};
            this.hasMore = true;
            this.isLoading = true;

            // Create new abort controller
            this.abortController = new AbortController();

            // Show loading state
            // this.container.innerHTML = '<div class="hr-loading">Loading...</div>';

            try {
                // Fetch data from Hello Retail API
                const data = await this.fetchSearchData(searchTerm, this.abortController.signal);

                // Check if request was aborted
                if (this.abortController.signal.aborted) {
                    return;
                }

                if (!data) {
                    this.container.innerHTML = '<p class="hr-error">Error loading search results. Please try again.</p>';
                    this.isLoading = false;
                    return;
                }

                // Check if searchTerm changed during fetch
                if (this.lastRenderSearchTerm !== searchTerm) {
                    console.log('HelloRetailSearchRenderer: SearchTerm changed during fetch, ignoring result');
                    return;
                }

                this.currentData = data;

                // Create HTML structure
                const html = this.createSearchLayout(data);
                this.container.innerHTML = html;

                // Initialize event listeners
                this.initializeEventListeners();

                // Setup infinite scroll
                this.setupInfiniteScroll();

                // Update URL with search parameter
                this.updateSearchUrl(searchTerm);

                this.isLoading = false;
            } catch (error) {
                // Ignore abort errors
                if (error.name === 'AbortError') {
                    console.log('HelloRetailSearchRenderer: Request aborted');
                    return;
                }

                console.error('Error rendering search results:', error);
                this.container.innerHTML = '<p class="hr-error">Error loading search results. Please try again.</p>';
                this.isLoading = false;
            }
        }

        /**
         * Update URL with search parameter
         */
        updateSearchUrl(searchTerm) {
            try {
                const url = new URL(window.location.href);
                
                if (searchTerm) {
                    // Set search parameter in format: search=(search_term:te)
                    url.searchParams.set('search', `(search_term:${encodeURIComponent(searchTerm)})`);
                } else {
                    // Remove search parameter if no search term
                    url.searchParams.delete('search');
                }
                
                // Update URL without reloading page
                window.history.replaceState({}, '', url.toString());
            } catch (error) {
                console.error('Error updating search URL:', error);
            }
        }

        /**
         * Load initial content from Hello Retail recoms API
         */
        async loadInitialContent() {
            if (this.isLoading) return;
            
            this.isLoading = true;
            this.searchTerm = '';
            this.currentPage = 1;
            this.activeFilters = {};
            this.hasMore = false; // Initial content doesn't support pagination

            try {
                // Fetch initial content from Hello Retail recoms API
                const data = await this.fetchRecomsData();
                
                if (!data || !data.responses || !data.responses[0] || !data.responses[0].products) {
                    this.isLoading = false;
                    return;
                }

                const products = data.responses[0].products || [];
                this.currentData = { result: products };

                // Create HTML structure for initial content (simplified layout)
                const html = this.createInitialContentLayout(products);
                this.container.innerHTML = html;

                // Remove search parameter from URL when showing initial content
                this.updateSearchUrl('');

                // Initialize event listeners for size selector triggers
                // Note: Initial content doesn't have filters/sorting, but still needs size selector handlers
                this.initializeSizeSelectorListeners();
            } catch (error) {
                console.error('Error loading initial content:', error);
            } finally {
                this.isLoading = false;
            }
        }

        /**
         * Fetch recommendations data from Hello Retail recoms API
         */
        async fetchRecomsData() {
            if (!this.websiteUuid || !this.initialContentProductBoxKey) {
                console.warn('HelloRetailSearchRenderer: websiteUuid or initialContentProductBoxKey not set');
                return null;
            }

            const searchContext = {};
            try {
                if (typeof window !== 'undefined' && window.location?.href) {
                    searchContext.urls = [window.location.href];
                }
            } catch (e) {
                /* ignore */
            }

            const requestItem = buildManagedRecomRequestItem({
                key: this.initialContentProductBoxKey,
                format: 'json',
                context: searchContext
            });

            try {
                const signal = this.abortController ? this.abortController.signal : null;
                return await postHelloRetailRecoms(this.websiteUuid, [requestItem], signal);
            } catch (error) {
                if (error && error.name === 'AbortError') {
                    return null;
                }
                console.error('Error fetching recoms data:', error);
                return null;
            }
        }

        /**
         * Create simplified layout for initial content (products only)
         */
        createInitialContentLayout(products) {
            const textPopularProducts = window.themeVariables?.strings?.helloRetailTextPopularProducts || 'Popular products';

            return `
        <div class="hr-search-results hr-initial-content">
          <div class="hr-search-layout">
            <!-- Main Content: Products only -->
            <main class="hr-main-content hr-main-content--full">
                <div class="hr-products-header">${textPopularProducts}</div>
              
              <div class="hr-product-grid" id="hr-product-grid">
                ${this.renderProducts(products)}
              </div>
            </main>
          </div>
        </div>
      `;
        }

        /**
         * Fetch search data from Hello Retail API
         */
        async fetchSearchData(searchTerm, signal = null) {
            const page = 1;
            const productCount = 20;
            const collectionCount = 10;

            const queryParams = new URLSearchParams({
                q: searchTerm,
                product_count: productCount,
                format: 'json',
                product_start: (page - 1) * productCount,
                return_filters: true,
                category_count: collectionCount
            });

            const fetchOptions = {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            };

            // Add signal if provided (for aborting requests)
            if (signal) {
                fetchOptions.signal = signal;
            }

            const response = await fetch(
                `https://core.helloretail.com/api/v1/search/partnerSearch?key=${this.searchConfigKey}&${queryParams}`,
                fetchOptions
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        }

        /**
         * Create the main search layout HTML
         */
        createSearchLayout(data) {
            // Safely extract data with proper defaults
            const categories = data.categories || [];
            const products = Array.isArray(data.result) ? data.result : [];
            const filters = data.filters || {};
            const filterTitles = data.filter_titles || {};
            const totalProducts = data.product_results || products.length;

            const textCategories = window.themeVariables?.strings?.helloRetailTextCategories || 'Categories';
            const textNoCategoriesFound = window.themeVariables?.strings?.helloRetailTextNoCategoriesFound || 'No categories found';
            const textProducts = window.themeVariables?.strings?.helloRetailTextProducts || 'Products';

            // Debug logging
            /*
            console.log('Hello Retail Search Data:', {
              categories: categories,
              productsCount: products.length,
              filters: filters,
              filtersType: typeof filters,
              isArray: Array.isArray(filters)
            });
            */

            return `
        <div class="hr-search-results">
          <div class="hr-search-header hidden">
            <h2 class="hr-search-title">
              ${this.searchTerm ? `Search results for "${this.searchTerm}"` : 'Search Results'}
            </h2>
            <p class="hr-search-count">${totalProducts} products found</p>
          </div>

          <div class="hr-search-layout">
            <!-- Sidebar: Categories only -->
            <aside class="hr-sidebar">
            <div class="hr-filter-section hr-categories-section">
                <h3 class="hr-filter-title">${textCategories}</h3>
                <div class="hr-category-list">
                    ${this.renderCategories(categories) || `<p>${textNoCategoriesFound}</p>`}
                </div>
              </div>
            </aside>

            <!-- Main Content: Filters & Products -->
            <main class="hr-main-content">
                <div class="hr-products-header">${textProducts}</div>
            
              <!-- Filters as dropdowns above product list -->
              <div class="hr-filters-bar">
                ${this.renderFilters(filters, filterTitles)}
                ${this.renderSorting()}
              </div>
              
              <div class="hr-product-grid" id="hr-product-grid">
                ${this.renderProducts(products)}
              </div>
              <div id="hr-load-more-sentinel"></div>
            </main>
          </div>
        </div>
      `;
        }

        /**
         * Render categories list
         */
        renderCategories(categories) {
            if (!categories) return '';

            // Ensure categories is an array
            let categoriesArray = [];
            if (Array.isArray(categories)) {
                categoriesArray = categories;
            } else if (typeof categories === 'object') {
                // Convert object to array if needed
                categoriesArray = Object.values(categories);
            }

            if (categoriesArray.length === 0) return '';

            const categoriesHtml = categoriesArray.map(category => {
                const categoryName = category.name || category.title || '';
                const categoryUrl = category.url || category.link || '#';
                const categoryCount = category.count || category.product_count || 0;

                return `
          <a href="${categoryUrl}" class="hr-category-item" data-category-id="${category.id || ''}">
            <span class="hr-category-name">${categoryName}</span>
            <span class="hr-category-count hidden">(${categoryCount})</span>
          </a>
        `;
            }).join('');

            return `
            ${categoriesHtml}
      `;
        }

        /**
         * Render filters
         */
        renderFilters(filters, filterTitles) {
            // Check if filters is an array, if not try to convert or return empty
            if (!filters) return '';

            // If filters is an object (not array), convert it to array format
            let filtersArray = [];
            if (Array.isArray(filters)) {
                filtersArray = filters;
            } else if (typeof filters === 'object') {
                // Convert object to array: {brand: {...}, price: {...}} -> [{key: 'brand', ...}, {key: 'price', ...}]
                filtersArray = Object.keys(filters).map(key => {
                    const filter = filters[key];
                    return {
                        key: key,
                        name: filter.name || filter.label || key,
                        options: filter.options || filter
                    };
                });
            }

            if (filtersArray.length === 0) return '';

            const filtersHtml = filtersArray.map(filter => {
                const filterKey = filter.key || filter.id || filter.field || '';
                const filterName = filterTitles[filterKey] || filter.name || filter.title || '';

                // Check if this is a price filter - render as range input
                const isPriceFilter = filterKey.toLowerCase().includes('price') ||
                    filterName.toLowerCase().includes('price') ||
                    filterKey === 'price';

                if (isPriceFilter) {
                    // Get min and max from filter options or use defaults
                    let minPrice = 0;
                    let maxPrice = 10000;

                    // Try to extract min/max from filter options
                    let filterOptions = filter.options || [];
                    if (typeof filterOptions === 'object' && filterOptions !== null) {
                        minPrice = filterOptions.min;
                        maxPrice = filterOptions.max;
                    }

                    // Convert to dollars (assuming prices are in cents from Hello Retail)
                    minPrice = Math.floor(minPrice);
                    maxPrice = Math.ceil(maxPrice);

                    // Get current price range from active filters (convert from cents to dollars for display)
                    const currentPriceRange = this.activeFilters[filterKey] || {};
                    const currentMin = currentPriceRange.min ? Math.floor(currentPriceRange.min) : minPrice;
                    const currentMax = currentPriceRange.max ? Math.ceil(currentPriceRange.max) : maxPrice;

                    // Calculate progress percentages for range sliders
                    const lowerBoundProgress = maxPrice > 0 ? (currentMin / maxPrice) * 100 : 0;
                    const higherBoundProgress = maxPrice > 0 ? (currentMax / maxPrice) * 100 : 100;

                    // Get currency symbol (fallback to $)
                    let currencySymbol = window.Shopify?.currency?.active || window.themeVariables?.settings?.currencyCode || '$';
                    if (currencySymbol === 'DKK') {
                        currencySymbol = 'kr';
                    }

                    const showCurrencyCode = window.themeVariables?.settings?.currencyCodeEnabled || false;
                    const currencyCode = window.Shopify?.currency?.active || '';
                    const priceRangeToText = window.themeVariables?.strings?.helloRetailTextPriceRangeTo || 'to';

                    // Format selected price range for display
                    let selectedPriceText = '';
                    if (currentPriceRange.min !== undefined || currentPriceRange.max !== undefined) {
                        const minText = currentPriceRange.min !== undefined ? `${currencySymbol}${currentMin}` : '';
                        const maxText = currentPriceRange.max !== undefined ? `${currencySymbol}${currentMax}` : '';
                        selectedPriceText = minText && maxText ? `${minText} - ${maxText}` : (minText || maxText);
                    }

                    return `
            <div class="hr-filter-dropdown hr-price-filter-dropdown" data-filter-type="${filterKey}">
              <button type="button" class="hr-filter-dropdown-toggle" aria-expanded="false">
                <span class="hr-filter-dropdown-label">${filterName}</span>
                ${selectedPriceText ? `<span class="hr-filter-selected">${selectedPriceText}</span>` : ''}
                <span class="hr-filter-dropdown-arrow">▼</span>
              </button>
              <div class="hr-filter-dropdown-menu hr-price-filter-menu" style="display: none;">
                <price-range class="price-range" data-hr-filter-key="${filterKey}">
                  <div class="range-group" style="--range-min: ${lowerBoundProgress}%; --range-max: ${higherBoundProgress}%">
                    <input type="range" aria-label="Price from" class="range" min="0" max="${maxPrice}" value="${currentMin}" data-hr-price-min="${filterKey}">
                    <input type="range" aria-label="Price to" class="range" min="0" max="${maxPrice}" value="${currentMax}" data-hr-price-max="${filterKey}">
                  </div>
                  <div class="price-range__input-group">
                    <label class="input-prefix text-sm">
                      <span class="prefix text-subdued">${currencySymbol}${showCurrencyCode ? currencyCode : ''}</span>
                      <input aria-label="Price from" class="field" type="number" inputmode="numeric" name="filter.v.price.gte" value="${currentMin}" data-hr-price-min-input="${filterKey}" min="0" max="${maxPrice}" placeholder="0" autocomplete="off">
                    </label>
                    <span class="subheading">${priceRangeToText}</span>
                    <label class="input-prefix text-sm">
                      <span class="prefix text-subdued">${currencySymbol}${showCurrencyCode ? currencyCode : ''}</span>
                      <input aria-label="Price to" class="field" type="number" inputmode="numeric" name="filter.v.price.lte" value="${currentMax}" data-hr-price-max-input="${filterKey}" min="${currentMin}" max="${maxPrice}" placeholder="${maxPrice}" autocomplete="off">
                    </label>
                  </div>
                </price-range>
              </div>
            </div>
          `;
                }

                // Regular filter with checkboxes
                // Ensure filterOptions is an array
                let filterOptions = filter.options || [];
                if (!Array.isArray(filterOptions)) {
                    if (typeof filterOptions === 'object' && filterOptions !== null) {
                        // Convert object to array: {value1: count1, value2: count2} -> [{value: 'value1', count: count1}, ...]
                        filterOptions = Object.keys(filterOptions).map(key => ({
                            value: key,
                            label: key,
                            count: filterOptions[key]
                        }));
                    } else {
                        filterOptions = [];
                    }
                }

                if (!filterKey || filterOptions.length === 0) return '';

                let sortedFilterOptions = [...filterOptions];

                if (filterKey === 'extraDataList.variant_metafields_sizes') {
                    // Helper functions for smart sorting (defined once, used for both priority and normal)
                    const detectSortingMode = (options) => {
                        // Handle both array of strings and array of objects
                        const allValues = options.map(opt => {
                            if (typeof opt === 'string') return opt.toLowerCase();
                            return (opt.title || opt.label || opt.value || '').toLowerCase();
                        });
                        
                        // Check for duration tokens
                        const durationPattern = /(mdr|month|months|mo|mos|år|aar|year|years|yr|yrs)/i;
                        const hasDuration = allValues.some(v => durationPattern.test(v));
                        if (hasDuration) return 'duration';
                        
                        // Check if mostly numeric (at least 50% have leading numbers)
                        const numericPattern = /^\s*[\d.,]+/;
                        const numericCount = allValues.filter(v => numericPattern.test(v)).length;
                        if (numericCount >= allValues.length * 0.5) return 'numeric';
                        
                        // Check for alpha size tokens
                        const alphaSizePattern = /\b(xxs|xs|s|m|l|xl|xxl|xxxl)\b/i;
                        const hasAlphaSize = allValues.some(v => alphaSizePattern.test(v));
                        if (hasAlphaSize) return 'alpha';
                        
                        return 'default';
                    };

                    const parseDurationToMonths = (value) => {
                        const lowerValue = value.toLowerCase().trim();
                        
                        // Month tokens: mdr, month, months, mo, mos
                        // Match number (with optional decimal) followed by month token
                        // Token can be followed by space, /, comma, dash, or end of string
                        const monthPattern = /(\d+(?:[.,]\d+)?)\s*(mdr|month|months|mo|mos)(?:\s|\/|,|-|$)/i;
                        const monthMatch = lowerValue.match(monthPattern);
                        if (monthMatch) {
                            const months = parseFloat(monthMatch[1].replace(',', '.'));
                            return isNaN(months) ? null : months;
                        }
                        
                        // Year tokens: år, aar, year, years, yr, yrs
                        // Use Unicode-aware matching for år (not \b - match explicitly)
                        // Match år followed by space, /, comma, dash, or end (Unicode-aware)
                        const yearPattern = /(\d+(?:[.,]\d+)?)\s*(år|aar|year|years|yr|yrs)(?:\s|\/|,|-|$)/i;
                        const yearMatch = lowerValue.match(yearPattern);
                        if (yearMatch) {
                            const years = parseFloat(yearMatch[1].replace(',', '.'));
                            return isNaN(years) ? null : years * 12;
                        }
                        
                        return null;
                    };

                    const extractNumericValue = (value) => {
                        // Try to match first number (can be anywhere in string, not just at start)
                        // Handles patterns like "15-17", "17", "STRØMPER STR. 86", etc.
                        const match = value.match(/\b(\d+(?:[.,]\d+)?)/);
                        if (match) {
                            return parseFloat(match[1].replace(',', '.'));
                        }
                        return null;
                    };

                    const getAlphaSizeRank = (value) => {
                        const lowerValue = value.toLowerCase();
                        const alphaSizeOrder = ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl'];
                        
                        // Try to find size token in the value
                        for (let i = 0; i < alphaSizeOrder.length; i++) {
                            const sizeToken = alphaSizeOrder[i];
                            // Use word boundary or check if it's a standalone word
                            const pattern = new RegExp(`\\b${sizeToken}\\b`, 'i');
                            if (pattern.test(lowerValue)) {
                                return i;
                            }
                        }
                        
                        return null;
                    };

                    // Extract prefix from a size value (text before the size value)
                    const extractPrefix = (value) => {
                        const str = typeof value === 'string' ? value : (value.title || value.label || value.value || '');
                        
                        // First, try to find duration pattern (most specific)
                        const durationPattern = /(\d+(?:[.,]\d+)?)\s*(mdr|month|months|mo|mos|år|aar|year|years|yr|yrs)/i;
                        const durationMatch = str.match(durationPattern);
                        if (durationMatch && durationMatch.index > 0) {
                            return str.substring(0, durationMatch.index).trim();
                        }
                        
                        // Then, try to find numeric pattern (number or number range)
                        const numericPattern = /\b(\d+(?:[.,]\d+)?(?:-\d+(?:[.,]\d+)?)?)\b/;
                        const numericMatch = str.match(numericPattern);
                        if (numericMatch && numericMatch.index > 0) {
                            return str.substring(0, numericMatch.index).trim();
                        }
                        
                        // Then, try to find alpha size pattern
                        const alphaSizePattern = /\b(xxs|xs|s|m|l|xl|xxl|xxxl)\b/i;
                        const alphaMatch = str.match(alphaSizePattern);
                        if (alphaMatch && alphaMatch.index > 0) {
                            return str.substring(0, alphaMatch.index).trim();
                        }
                        
                        // For other cases, return empty - will be handled in second pass
                        return '';
                    };

                    // Smart sort function for sorting within a group (takes sortingMode as parameter)
                    const smartSort = (sortingMode) => {
                        return (a, b) => {
                            // Handle both objects and strings
                            const valueA = typeof a === 'string' ? a : (a.title || a.label || a.value || '');
                            const valueB = typeof b === 'string' ? b : (b.title || b.label || b.value || '');
                            
                            if (sortingMode === 'duration') {
                                const monthsA = parseDurationToMonths(valueA);
                                const monthsB = parseDurationToMonths(valueB);
                                
                                // If both have duration, compare by months
                                if (monthsA !== null && monthsB !== null) {
                                    const diff = monthsA - monthsB;
                                    if (diff !== 0) {
                                        return diff;
                                    }
                                    // If durations are equal, fall through to numeric comparison
                                }
                                
                                // If only one has duration, prioritize it
                                if (monthsA !== null && monthsB === null) return -1;
                                if (monthsA === null && monthsB !== null) return 1;
                                
                                // If neither has duration or both equal, try numeric fallback
                                const numA = extractNumericValue(valueA);
                                const numB = extractNumericValue(valueB);
                                if (numA !== null && numB !== null) {
                                    const numDiff = numA - numB;
                                    if (numDiff !== 0) {
                                        return numDiff;
                                    }
                                }
                                
                                // Fallback to alphabetical
                                return valueA.localeCompare(valueB, 'da', { numeric: true });
                            }
                            
                            if (sortingMode === 'numeric') {
                                const numA = extractNumericValue(valueA);
                                const numB = extractNumericValue(valueB);
                                
                                if (numA !== null && numB !== null) {
                                    if (numA !== numB) {
                                        return numA - numB;
                                    }
                                }
                                
                                // Fallback to alphabetical for equal or non-numeric
                                return valueA.localeCompare(valueB, 'da', { numeric: true });
                            }
                            
                            if (sortingMode === 'alpha') {
                                const rankA = getAlphaSizeRank(valueA);
                                const rankB = getAlphaSizeRank(valueB);
                                
                                if (rankA !== null && rankB !== null) {
                                    return rankA - rankB;
                                }
                                
                                // If only one has alpha size, prioritize it
                                if (rankA !== null && rankB === null) return -1;
                                if (rankA === null && rankB !== null) return 1;
                                
                                // Fallback to alphabetical
                                return valueA.localeCompare(valueB, 'da', { numeric: true });
                            }
                            
                            // Default: alphabetical
                            return valueA.localeCompare(valueB, 'da', { numeric: true });
                        };
                    };

                    // Group options by prefix (first pass)
                    const groups = new Map();
                    const ungroupedItems = [];
                    
                    filterOptions.forEach(option => {
                        const value = typeof option === 'string' ? option : (option.title || option.label || option.value || '');
                        const prefix = extractPrefix(value);
                        const groupKey = prefix || '__NO_PREFIX__';
                        
                        if (prefix === '') {
                            // Item without clear prefix - will be grouped in second pass
                            ungroupedItems.push(option);
                        } else {
                            if (!groups.has(groupKey)) {
                                groups.set(groupKey, []);
                            }
                            groups.get(groupKey).push(option);
                        }
                    });
                    
                    // Second pass: try to group ungrouped items with existing groups
                    // by checking if they start with a known prefix
                    ungroupedItems.forEach(option => {
                        const value = typeof option === 'string' ? option : (option.title || option.label || option.value || '');
                        let matched = false;
                        
                        // Check if item starts with any known prefix
                        for (const [prefix, groupItems] of groups.entries()) {
                            if (prefix !== '__NO_PREFIX__' && value.toLowerCase().startsWith(prefix.toLowerCase())) {
                                // Check if there's a size value after the prefix
                                const afterPrefix = value.substring(prefix.length).trim();
                                if (afterPrefix.length > 0) {
                                    groupItems.push(option);
                                    matched = true;
                                    break;
                                }
                            }
                        }
                        
                        // If no match found, add to no prefix group
                        if (!matched) {
                            if (!groups.has('__NO_PREFIX__')) {
                                groups.set('__NO_PREFIX__', []);
                            }
                            groups.get('__NO_PREFIX__').push(option);
                        }
                    });

                    // Helper function to detect type of individual item
                    const detectItemType = (value) => {
                        const str = typeof value === 'string' ? value : (value.title || value.label || value.value || '');
                        const lowerStr = str.toLowerCase();
                        
                        // Check for duration tokens
                        const durationPattern = /(mdr|month|months|mo|mos|år|aar|year|years|yr|yrs)/i;
                        if (durationPattern.test(lowerStr)) {
                            return 'duration';
                        }
                        
                        // Check for alpha size tokens
                        const alphaSizePattern = /\b(xxs|xs|s|m|l|xl|xxl|xxxl)\b/i;
                        if (alphaSizePattern.test(lowerStr)) {
                            return 'alpha';
                        }
                        
                        // Check if numeric: contains number pattern (can be anywhere, not just at start)
                        // Match patterns like "15-17", "17", "86", "92", etc.
                        const numericPattern = /\b\d+(?:[.,]\d+)?(?:-\d+(?:[.,]\d+)?)?\b/;
                        if (numericPattern.test(str)) {
                            return 'numeric';
                        }
                        
                        return 'default';
                    };

                    // Sort each group internally: duration first, then numeric, then alpha
                    groups.forEach((options, prefix) => {
                        // Classify items into categories
                        const durationItems = [];
                        const numericItems = [];
                        const alphaItems = [];
                        const defaultItems = [];
                        
                        options.forEach(option => {
                            const itemType = detectItemType(option);
                            if (itemType === 'duration') {
                                durationItems.push(option);
                            } else if (itemType === 'numeric') {
                                numericItems.push(option);
                            } else if (itemType === 'alpha') {
                                alphaItems.push(option);
                            } else {
                                defaultItems.push(option);
                            }
                        });
                        
                        // Sort each category
                        if (durationItems.length > 0) {
                            durationItems.sort(smartSort('duration'));
                        }
                        if (numericItems.length > 0) {
                            numericItems.sort(smartSort('numeric'));
                        }
                        if (alphaItems.length > 0) {
                            alphaItems.sort(smartSort('alpha'));
                        }
                        if (defaultItems.length > 0) {
                            defaultItems.sort(smartSort('default'));
                        }
                        
                        // Combine in order: duration → numeric → alpha → default
                        options.length = 0;
                        options.push(...durationItems, ...numericItems, ...alphaItems, ...defaultItems);
                    });

                    // Sort groups: no prefix first, then alphabetical by prefix
                    const sortedGroups = Array.from(groups.entries()).sort(([prefixA], [prefixB]) => {
                        // No prefix group always comes first
                        if (prefixA === '__NO_PREFIX__') return -1;
                        if (prefixB === '__NO_PREFIX__') return 1;
                        
                        // Compare prefixes alphabetically
                        return prefixA.localeCompare(prefixB, 'da');
                    });

                    // Flatten groups back into sorted array
                    sortedFilterOptions = sortedGroups.flatMap(([, options]) => options);
                } else {
                    // Sort all options alphabetically for other filters
                    sortedFilterOptions.sort((a, b) => {
                        const labelA = (a.title || '').toLowerCase();
                        const labelB = (b.title || '').toLowerCase();
                        return labelA.localeCompare(labelB);
                    });
                }

                // Get selected options for display
                const selectedOptions = this.activeFilters[filterKey] || [];
                const selectedLabels = sortedFilterOptions
                    .filter(opt => selectedOptions.includes(opt.query || ''))
                    .map(opt => opt.title || '')
                    .join(', ');

                const optionsHtml = sortedFilterOptions.map(option => {
                    const optionLabel = option.title || '';
                    const optionValue = option.query || '';
                    const optionCount = option.count || 0;
                    const isChecked = this.activeFilters[filterKey]?.includes(optionValue) || false;

                    return `
            <label class="hr-filter-dropdown-option">
              <input 
                type="checkbox" 
                value="${optionValue}" 
                data-filter-key="${filterKey}"
                ${isChecked ? 'checked' : ''}
              >
              <span class="hr-filter-option-label">${optionLabel}</span>
              <span class="hr-filter-option-count">(${optionCount})</span>
            </label>
          `;
                }).join('');

                return `
          <div class="hr-filter-dropdown" data-filter-type="${filterKey}">
            <button type="button" class="hr-filter-dropdown-toggle" aria-expanded="false">
              <span class="hr-filter-dropdown-label">${filterName}</span>
              ${selectedLabels ? `<span class="hr-filter-selected">${selectedLabels}</span>` : ''}
              <span class="hr-filter-dropdown-arrow">▼</span>
            </button>
            <div class="hr-filter-dropdown-menu" style="display: none;">
              <div class="hr-filter-dropdown-options">
                ${optionsHtml}
              </div>
            </div>
          </div>
        `;
            }).join('');

            return filtersHtml;
        }

        /**
         * Render sorting options
         */
        renderSorting() {
            const textSortBy = window.themeVariables?.strings?.helloRetailTextSortBy || 'Sort by';
            const textMostPopular = window.themeVariables?.strings?.helloRetailTextMostPopular || 'Most popular';
            const textAlphabeticalAsc = window.themeVariables?.strings?.helloRetailTextAlphabeticalAsc || 'Alphabetical, A-Z';
            const textAlphabeticalDesc = window.themeVariables?.strings?.helloRetailTextAlphabeticalDesc || 'Alphabetical, Z-A';
            const textPriceLowToHigh = window.themeVariables?.strings?.helloRetailTextPriceLowToHigh || 'Price, low to high';
            const textPriceHighToLow = window.themeVariables?.strings?.helloRetailTextPriceHighToLow || 'Price, high to low';
            const textDateOldToNew = window.themeVariables?.strings?.helloRetailTextDateOldToNew || 'Date, old to new';
            const textDateNewToOld = window.themeVariables?.strings?.helloRetailTextDateNewToOld || 'Date, new to old';

            const sortOptions = [
                { value: '', label: textSortBy },
                { value: 'extraData.sortByTitle asc', label: textAlphabeticalAsc },
                { value: 'extraData.sortByTitle desc', label: textAlphabeticalDesc },
                { value: 'price asc', label: textPriceLowToHigh },
                { value: 'price desc', label: textPriceHighToLow },
                { value: 'created asc', label: textDateOldToNew },
                { value: 'created desc', label: textDateNewToOld }
            ];

            const sortOptionsHtml = sortOptions.map(option => {
                return `
          <option value="${option.value}" ${this.currentSort === option.value ? 'selected' : ''}>
            ${option.label}
          </option>
        `;
            }).join('');

            return `
        <div class="hr-sorting">
          <select id="hr-sort-select" class="hr-sort-select">
            ${sortOptionsHtml}
          </select>
        </div>
      `;
        }

        /**
         * Render products grid
         */
        renderProducts(products) {
            if (!products || products.length === 0) {
                const textNoProductsFound = window.themeVariables?.strings?.helloRetailTextNoProductsFound || 'No products found';
                return `<p class="hr-no-products">${textNoProductsFound}</p>`;
            }

            return products.map(product => this.renderProductCard(product)).join('');
        }

        /**
         * Render single product card
         */
        renderProductCard(product) {
            const productUrl = product.url || '#';
            const productTitle = product.title || '';
            const productImage = product.imgUrl || '';
            const productAltImage = product.extraData.altImage || '';
            const productPrice = product.price || 0;
            const productComparePrice = product.oldPrice || null;
            const productIsOnSale = product.isOnSale || false;
            const salePercentage = productIsOnSale ? Math.round(((productComparePrice - productPrice) / productComparePrice) * 100) : 0;
            const productVendor = product.vendor || product.brand || '';
            const productId = product.id || product.product_id || '';
            const productSizes = product.extraDataList?.V_collection_size || [];
            const strictStringifiedVariantData = product.extraData?.strictStringifiedVariantData || '[]';

            const textChooseSize = window.themeVariables?.strings?.helloRetailTextChooseSize || 'Choose size';
            const textSave = window.themeVariables?.strings?.helloRetailTextSave || 'Save';

            let currencySymbol = product.currency || '$';
            if (currencySymbol === 'DKK') {
                currencySymbol = 'kr';
            }
            const formatPrice = (price) => {
                if (typeof price === 'number') {
                    return price + ' ' + currencySymbol;
                }
                return price;
            };

            // Parse strictStringifiedVariantData
            let variants = [];
            try {
                if (typeof strictStringifiedVariantData === 'string' && strictStringifiedVariantData.trim()) {
                    variants = JSON.parse(strictStringifiedVariantData);
                } else if (Array.isArray(strictStringifiedVariantData)) {
                    variants = strictStringifiedVariantData;
                }
            } catch (e) {
                console.error('Error parsing strictStringifiedVariantData:', e);
            }

            // Extract variant ID from GID format: gid://shopify/ProductVariant/55759642296699 -> 55759642296699
            const extractVariantId = (gid) => {
                if (typeof gid === 'string' && gid.includes('/ProductVariant/')) {
                    return gid.split('/ProductVariant/')[1];
                }
                return gid;
            };

            // Render variants with add to cart forms
            const renderVariants = () => {
                if (!variants || variants.length === 0) {
                    return '';
                }

                return `
                    <div class="hr-product-sizes">
                        <p class="size-selector-title">
                            <span class="size-selector-icon">
                                <svg aria-hidden="true" focusable="false" width="13" class="icon icon-plus" style="--icon-mobile-max-width: 13px; --icon-max-width: 18px" part="icon" viewBox="0 0 14 14" fill="none">
                                    <path d="M7 0V14M0 7H14" stroke="currentColor" stroke-width="1" stroke-linecap="round"></path>
                                </svg>
                            </span>
                            <span>${textChooseSize}</span>
                        </p>
                        <div class="size-selector-close-trigger">
                            <svg role="presentation" viewBox="0 0 16 14">
                                <path d="M15 0L1 14m14 0L1 0" stroke="currentColor" fill="none" fill-rule="evenodd"></path>
                            </svg>
                        </div>
                        <ul class="hr-variant-list">
                            ${variants.map((variant, index) => {
                                const variantId = extractVariantId(variant.id);
                                const variantTitle = variant.title || '';
                                const inventoryQuantity = variant.inventoryQuantity || 0;
                                const isAvailable = inventoryQuantity > 0;
                                const formId = `hr-product-form-${productId}-${variantId}-${index}`;

                                return `
                                    <li class="hr-variant-item ${!isAvailable ? 'hr-variant-item--sold-out' : ''}">
                                        <product-form class="hr-variant-form">
                                            <form action="${window.Shopify?.routes?.root || ''}cart/add" method="post" id="${formId}">
                                                <input type="hidden" name="id" value="${variantId}">
                                                <button 
                                                    type="submit" 
                                                    class="hr-variant-button"
                                                    ${!isAvailable ? 'disabled' : ''}
                                                    title="${variantTitle}"
                                                >
                                                    <button-content>
                                                        <span class="hr-variant-title">${variantTitle}</span>
                                                    </button-content>
                                                </button>
                                            </form>
                                        </product-form>
                                    </li>
                                `;
                            }).join('')}
                        </ul>
                    </div>
                    <div class="size-selector-open-trigger">
                        <div class="size-selector-icon">
                            <svg aria-hidden="true" focusable="false" width="13" class="icon icon-plus" style="--icon-mobile-max-width: 13px; --icon-max-width: 18px" part="icon" viewBox="0 0 14 14" fill="none">
                                <path d="M7 0V14M0 7H14" stroke="currentColor" stroke-width="1" stroke-linecap="round"></path>
                            </svg>
                        </div>
                    </div>
                `;
            };

            return `
        <div class="hr-product-card product-card" data-product-id="${productId}">
          <a href="${productUrl}" class="hr-product-link">
            <div class="hr-image-wrapper">
              ${productAltImage ? `
                <img 
                  src="${productImage}" 
                  alt="${productTitle}"
                  loading="lazy"
                  class="hr-product-image main-image"
                >
                  <img 
                    src="${productAltImage}" 
                    alt="${productTitle}"
                    loading="lazy"
                    class="hr-product-image alt-image"
                  >
              ` : `
                <img 
                  src="${productImage}" 
                  alt="${productTitle}"
                  loading="lazy"
                  class="hr-product-image"
                >
              `}
              
              ${productIsOnSale ? `
                <span class="hr-badge hr-badge--sale">${textSave} ${salePercentage}%</span>
              ` : ''}
            </div>

            <div class="hr-product-info">
              ${productVendor ? `
                <p class="hr-product-vendor text-subdued">${productVendor}</p>
              ` : ''}
              
              <p class="hr-product-title">
                ${productTitle}
              </p>

              <div class="hr-product-price">
                <span class="hr-current-price">
                  ${formatPrice(productPrice)}
                </span>
                ${productComparePrice && productComparePrice > productPrice ? `
                  <span class="hr-compare-price">
                    <s>${formatPrice(productComparePrice)}</s>
                  </span>
                ` : ''}
              </div>
            </div>
          </a>
          
          ${variants.length > 0 ? renderVariants() : (productSizes.length > 0 ? `
                <div class="hr-product-sizes">
                    <p class="size-selector-title">
                        <span class="size-selector-icon">
                            <svg aria-hidden="true" focusable="false" width="13" class="icon icon-plus" style="--icon-mobile-max-width: 13px; --icon-max-width: 18px" part="icon" viewBox="0 0 14 14" fill="none">
                                <path d="M7 0V14M0 7H14" stroke="currentColor" stroke-width="1" stroke-linecap="round"></path>
                            </svg>
                        </span>
                        <span>${textChooseSize}</span>
                    </p>
                    <ul>
                        ${productSizes.map(size => `
                            <li title="${size}">
                                <span>${size}</span>
                            </li>
                        `).join('')}
                    </ul>
                </div>
            ` : '')}
        </div>
      `;
        }

        /**
         * Initialize event listeners
         */
        initializeEventListeners() {
            // Sort select
            const sortSelect = this.container.querySelector('#hr-sort-select');
            if (sortSelect) {
                sortSelect.addEventListener('change', (e) => {
                    this.currentSort = e.target.value;
                    this.currentPage = 1;
                    this.loadProducts(true);
                });
            }

            // Filter dropdown toggles
            const filterDropdownToggles = this.container.querySelectorAll('.hr-filter-dropdown-toggle');
            filterDropdownToggles.forEach(toggle => {
                toggle.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const dropdown = toggle.closest('.hr-filter-dropdown');
                    const menu = dropdown.querySelector('.hr-filter-dropdown-menu');
                    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

                    // Close all other dropdowns
                    filterDropdownToggles.forEach(otherToggle => {
                        if (otherToggle !== toggle) {
                            otherToggle.setAttribute('aria-expanded', 'false');
                            otherToggle.closest('.hr-filter-dropdown').querySelector('.hr-filter-dropdown-menu').style.display = 'none';
                        }
                    });

                    // Toggle current dropdown
                    toggle.setAttribute('aria-expanded', !isExpanded);
                    menu.style.display = !isExpanded ? 'block' : 'none';
                });
            });

            // Close dropdowns when clicking outside
            document.addEventListener('click', (e) => {
                if (!e.target.closest('.hr-filter-dropdown')) {
                    filterDropdownToggles.forEach(toggle => {
                        toggle.setAttribute('aria-expanded', 'false');
                        toggle.closest('.hr-filter-dropdown').querySelector('.hr-filter-dropdown-menu').style.display = 'none';
                    });
                }
            });

            // Filter checkboxes
            const filterCheckboxes = this.container.querySelectorAll('input[type="checkbox"][data-filter-key]');
            filterCheckboxes.forEach(checkbox => {
                checkbox.addEventListener('change', () => {
                    this.handleFilterChange();
                });
            });

            // Price range - listen to change events from theme's price-range component
            const priceRangeElements = this.container.querySelectorAll('price-range[data-hr-filter-key]');
            priceRangeElements.forEach(priceRangeEl => {
                const filterKey = priceRangeEl.getAttribute('data-hr-filter-key');
                const minInput = priceRangeEl.querySelector(`input[data-hr-price-min-input="${filterKey}"]`);
                const maxInput = priceRangeEl.querySelector(`input[data-hr-price-max-input="${filterKey}"]`);

                if (minInput && maxInput) {
                    // Listen to change events on the number inputs
                    minInput.addEventListener('change', () => {
                        this.handlePriceRangeChange(filterKey);
                    });
                    maxInput.addEventListener('change', () => {
                        this.handlePriceRangeChange(filterKey);
                    });
                }
            });

            // Initialize size selector listeners
            this.initializeSizeSelectorListeners();
        }

        /**
         * Initialize size selector event listeners
         * Uses event delegation to handle dynamically added elements
         * Works for both search results and recommendations boxes
         */
        initializeSizeSelectorListeners() {
            // Only initialize once to avoid duplicate listeners
            if (this.sizeSelectorListenersInitialized) return;
            
            // Size selector triggers - use event delegation on document level
            // This ensures it works for search results, initial content, and recommendations boxes
            document.addEventListener('click', (e) => {
                // Handle open trigger
                if (e.target.closest('.size-selector-open-trigger')) {
                    e.preventDefault();
                    e.stopPropagation();
                    const productCard = e.target.closest('.hr-product-card');
                    if (productCard) {
                        productCard.classList.add('open-size-selector');
                    }
                }
                
                // Handle close trigger
                if (e.target.closest('.size-selector-close-trigger')) {
                    e.preventDefault();
                    e.stopPropagation();
                    const productCard = e.target.closest('.hr-product-card');
                    if (productCard) {
                        productCard.classList.remove('open-size-selector');
                    }
                }
            });
            
            this.sizeSelectorListenersInitialized = true;
        }

        /**
         * Handle filter changes
         */
        handleFilterChange() {
            const filterCheckboxes = this.container.querySelectorAll('input[type="checkbox"][data-filter-key]');

            // Reset filters but keep price range
            const priceFilters = {};
            Object.keys(this.activeFilters).forEach(key => {
                if (key.toLowerCase().includes('price') || key === 'price') {
                    priceFilters[key] = this.activeFilters[key];
                }
            });
            this.activeFilters = priceFilters;

            filterCheckboxes.forEach(checkbox => {
                if (checkbox.checked) {
                    const filterKey = checkbox.getAttribute('data-filter-key');
                    if (!this.activeFilters[filterKey]) {
                        this.activeFilters[filterKey] = [];
                    }
                    this.activeFilters[filterKey].push(checkbox.value);
                }
            });

            // Update selected labels in dropdowns
            this.updateFilterDropdownLabels();

            this.currentPage = 1;
            this.loadProducts(true);
        }

        /**
         * Update selected labels in filter dropdowns
         */
        updateFilterDropdownLabels() {
            const dropdowns = this.container.querySelectorAll('.hr-filter-dropdown');
            dropdowns.forEach(dropdown => {
                const filterKey = dropdown.getAttribute('data-filter-type');
                const selectedOptions = this.activeFilters[filterKey] || [];

                if (selectedOptions.length === 0) {
                    const selectedSpan = dropdown.querySelector('.hr-filter-selected');
                    if (selectedSpan) {
                        selectedSpan.remove();
                    }
                    return;
                }

                // Get labels for selected options
                const checkboxes = dropdown.querySelectorAll(`input[type="checkbox"][data-filter-key="${filterKey}"]`);
                const selectedLabels = Array.from(checkboxes)
                    .filter(cb => selectedOptions.includes(cb.value))
                    .map(cb => {
                        const optionLabel = cb.closest('.hr-filter-dropdown-option').querySelector('.hr-filter-option-label');
                        return optionLabel ? optionLabel.textContent.trim() : '';
                    })
                    .filter(label => label)
                    .join(', ');

                // Update or create selected span
                let selectedSpan = dropdown.querySelector('.hr-filter-selected');
                if (selectedLabels) {
                    if (!selectedSpan) {
                        selectedSpan = document.createElement('span');
                        selectedSpan.className = 'hr-filter-selected';
                        const label = dropdown.querySelector('.hr-filter-dropdown-label');
                        if (label) {
                            label.after(selectedSpan);
                        }
                    }
                    selectedSpan.textContent = selectedLabels;
                } else if (selectedSpan) {
                    selectedSpan.remove();
                }
            });
        }

        /**
         * Handle price range changes
         */
        handlePriceRangeChange(filterKey) {
            const priceRangeEl = this.container.querySelector(`price-range[data-hr-filter-key="${filterKey}"]`);
            if (!priceRangeEl) return;

            const minInput = priceRangeEl.querySelector(`input[data-hr-price-min-input="${filterKey}"]`);
            const maxInput = priceRangeEl.querySelector(`input[data-hr-price-max-input="${filterKey}"]`);

            if (minInput && maxInput) {
                // Get values in dollars (from input)
                const minValueDollars = parseFloat(minInput.value);
                const maxValueDollars = parseFloat(maxInput.value);

                // Convert to cents for API (Hello Retail typically uses cents)
                const minValue = !isNaN(minValueDollars) ? Math.floor(minValueDollars) : null;
                const maxValue = !isNaN(maxValueDollars) ? Math.ceil(maxValueDollars) : null;

                // Validate and set filter
                if (minValue !== null && maxValue !== null && minValue <= maxValue) {
                    this.activeFilters[filterKey] = {
                        min: minValue,
                        max: maxValue
                    };
                } else if (minValue !== null || maxValue !== null) {
                    // If only one value is set, still apply it
                    if (!this.activeFilters[filterKey]) {
                        this.activeFilters[filterKey] = {};
                    }
                    if (minValue !== null) {
                        this.activeFilters[filterKey].min = minValue;
                    }
                    if (maxValue !== null) {
                        this.activeFilters[filterKey].max = maxValue;
                    }
                } else {
                    // Clear price filter if both are empty
                    delete this.activeFilters[filterKey];
                }

                this.currentPage = 1;
                this.loadProducts(true);
            }
        }

        /**
         * Setup infinite scroll
         */
        setupInfiniteScroll() {
            const sentinel = this.container.querySelector('#hr-load-more-sentinel');
            if (!sentinel) return;

            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && !this.isLoading && this.hasMore) {
                        this.loadMoreProducts();
                    }
                });
            }, {
                rootMargin: '100px'
            });

            observer.observe(sentinel);
        }

        /**
         * Load more products (infinite scroll)
         */
        async loadMoreProducts() {
            if (this.isLoading || !this.hasMore) return;

            this.currentPage++;
            await this.loadProducts(false);
        }

        /**
         * Load products from API
         */
        async loadProducts(replace = true) {
            if (this.isLoading) return;

            this.isLoading = true;
            const productGrid = this.container.querySelector('#hr-product-grid');

            if (replace) {
                // productGrid.innerHTML = '<div class="hr-loading">Loading...</div>';
            }

            try {
                const queryParams = new URLSearchParams({
                    q: this.searchTerm,
                    product_count: this.productCount,
                    format: 'json',
                    product_start: (this.currentPage - 1) * this.productCount,
                    return_filters: true,
                    category_count: 20
                });

                // Add filters to query params
                Object.keys(this.activeFilters).forEach(filterKey => {
                    const filterValue = this.activeFilters[filterKey];

                    // Check if this is a price range filter (object with min/max)
                    if (typeof filterValue === 'object' && filterValue !== null && !Array.isArray(filterValue)) {
                        // Price range format: "min-max" or "price:min-max"
                        if (filterValue.min !== undefined || filterValue.max !== undefined) {
                            const min = filterValue.min !== undefined ? filterValue.min : '';
                            const max = filterValue.max !== undefined ? filterValue.max : '';
                            queryParams.append(`filters[]`, `price:${min},${max}`);
                        }
                    } else if (Array.isArray(filterValue)) {
                        // Regular filter with multiple values
                        filterValue.forEach(value => {
                            queryParams.append(`filters[]`, value);
                        });
                    } else {
                        // Single value
                        queryParams.append(`filters[]`, filterValue);
                    }
                });

                // Add sorting to query params
                queryParams.append('sorting[]', this.currentSort);

                const response = await fetch(
                    `https://core.helloretail.com/api/v1/search/partnerSearch?key=${this.searchConfigKey}&${queryParams}`,
                    {
                        method: 'POST',
                        credentials: 'include',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json'
                        }
                    }
                );

                const data = await response.json();
                const products = data.result || [];

                if (replace) {
                    productGrid.innerHTML = this.renderProducts(products);
                } else {
                    productGrid.innerHTML += this.renderProducts(products);
                }

                // Check if there are more products
                const totalProducts = data.product_results || products.length;
                const loadedProducts = this.currentPage * this.productCount;
                this.hasMore = loadedProducts < totalProducts;

                if (!this.hasMore) {
                    const sentinel = this.container.querySelector('#hr-load-more-sentinel');
                    if (sentinel) {
                        sentinel.innerHTML = '<p class="hr-no-more hidden">No more products</p>';
                    }
                }

            } catch (error) {
                console.error('Error loading products:', error);
                if (productGrid) {
                    productGrid.innerHTML = '<p class="hr-error">Error loading products. Please try again.</p>';
                }
            } finally {
                this.isLoading = false;
            }
        }
    }

    // Export for use in theme.js
    window.HelloRetailSearchRenderer = HelloRetailSearchRenderer;

    // Global instance helper
    window.getHelloRetailSearchRenderer = function (container) {
        if (!window._helloRetailRendererInstance) {
            if (!container) {
                console.warn('HelloRetailSearchRenderer: Container is required for first initialization');
                return null;
            }
            window._helloRetailRendererInstance = new HelloRetailSearchRenderer(container);
        } else if (container && window._helloRetailRendererInstance.container !== container) {
            // Update container if different
            window._helloRetailRendererInstance.container = container;
        }
        return window._helloRetailRendererInstance;
    };

    // Load initial content when search drawer opens
    function loadInitialContent() {
        const searchDrawer = document.getElementById('search-drawer');
        const predictiveSearch = document.getElementById('predictive-search');
        
        if (!searchDrawer || !predictiveSearch) return;
        
        // Check if Hello Retail is enabled
        if (!window.themeVariables?.settings?.helloRetailEnable) return;
        
        // Check if drawer is open
        if (!searchDrawer.open && !searchDrawer.hasAttribute('open')) return;
        
        // Check if input has value - if yes, don't load initial content
        const searchInput = document.getElementById('search-drawer-input');
        if (searchInput && searchInput.value && searchInput.value.trim() !== '') {
            return;
        }
        
        // Ensure shadowRoot has slot="results" (similar to doPredictiveSearch_fn)
        if (predictiveSearch.shadowRoot) {
            predictiveSearch.shadowRoot.innerHTML = `<slot name="results"></slot>`;
        }
        
        const resultsContainer = predictiveSearch.querySelector('[slot="results"]');
        if (!resultsContainer) return;
        
        // Ensure results container has display: contents
        if (!resultsContainer.classList.contains('contents')) {
            resultsContainer.classList.add('contents');
        }
        resultsContainer.style.display = 'contents';
        
        // Check if initial content already loaded for this session
        if (resultsContainer.hasAttribute('data-initial-loaded')) return;
        
        // Get renderer instance
        const renderer = window.getHelloRetailSearchRenderer(resultsContainer);
        if (!renderer) return;
        
        // Fetch initial content from recoms API
        renderer.loadInitialContent().then(() => {
            resultsContainer.setAttribute('data-initial-loaded', 'true');
        }).catch(error => {
            console.error('Error loading initial content:', error);
        });
    }

    // Setup initial content loader
    function setupInitialContentLoader() {
        const searchDrawer = document.getElementById('search-drawer');
        const predictiveSearch = document.getElementById('predictive-search');
        
        if (!searchDrawer || !predictiveSearch) return;
        
        // Listen for drawer show/open events
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
                    if (searchDrawer.open || searchDrawer.hasAttribute('open')) {
                        // Drawer opened, load initial content after a short delay
                        setTimeout(() => {
                            loadInitialContent();
                        }, 200);
                    } else {
                        // Drawer closed, reset the flag so initial content can load again next time
                        const resultsContainer = predictiveSearch.querySelector('[slot="results"]');
                        if (resultsContainer) {
                            resultsContainer.removeAttribute('data-initial-loaded');
                        }
                    }
                }
            });
        });
        
        observer.observe(searchDrawer, {
            attributes: true,
            attributeFilter: ['open']
        });
        
        // Also listen for custom dialog events
        searchDrawer.addEventListener('dialog:after-show', () => {
            setTimeout(() => {
                loadInitialContent();
            }, 200);
        });
        
        // Check if drawer is already open on page load
        if (searchDrawer.open || searchDrawer.hasAttribute('open')) {
            setTimeout(() => {
                loadInitialContent();
            }, 300);
        }
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setupInitialContentLoader);
    } else {
        // DOM already loaded
        setTimeout(setupInitialContentLoader, 100);
    }

    /**
     * Parse search term from URL parameter
     * Format: search=(search_term:te) where "te" is the search term
     */
    function parseSearchTermFromUrl() {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const searchParam = urlParams.get('search');
            
            if (!searchParam) return null;
            
            // Parse format: (search_term:te)
            const match = searchParam.match(/\(search_term:(.+)\)/);
            if (match && match[1]) {
                return decodeURIComponent(match[1]);
            }
            
            return null;
        } catch (error) {
            console.error('Error parsing search term from URL:', error);
            return null;
        }
    }

    /**
     * Open search drawer and trigger search with term from URL
     */
    function handleSearchFromUrl() {
        // Check if Hello Retail is enabled
        if (!window.themeVariables?.settings?.helloRetailEnable) return;
        
        const searchTerm = parseSearchTermFromUrl();
        if (!searchTerm) return;
        
        // Wait for DOM to be ready
        const initSearch = () => {
            const searchDrawer = document.getElementById('search-drawer');
            const searchInput = document.getElementById('search-drawer-input');
            const predictiveSearch = document.getElementById('predictive-search');
            
            if (!searchDrawer || !searchInput || !predictiveSearch) {
                // Retry after a short delay if elements not ready
                setTimeout(initSearch, 100);
                return;
            }
            
            // Set search term in input
            searchInput.value = searchTerm;
            
            // Open search drawer
            if (searchDrawer.show) {
                searchDrawer.show();
            } else {
                searchDrawer.setAttribute('open', '');
            }
            
            // Wait for drawer to open, then trigger search
            setTimeout(() => {
                // Ensure shadowRoot has slot="results"
                if (predictiveSearch.shadowRoot) {
                    predictiveSearch.shadowRoot.innerHTML = `<slot name="results"></slot>`;
                }
                
                // Trigger search by calling doPredictiveSearch if available
                if (predictiveSearch.doPredictiveSearch && typeof predictiveSearch.doPredictiveSearch === 'function') {
                    predictiveSearch.doPredictiveSearch();
                } else {
                    // Fallback: dispatch input event to trigger search
                    const inputEvent = new Event('input', { bubbles: true });
                    searchInput.dispatchEvent(inputEvent);
                }
            }, 300);
        };
        
        // Start initialization
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', initSearch);
        } else {
            setTimeout(initSearch, 100);
        }
    }

    // Check URL and open search drawer if search param exists
    handleSearchFromUrl();

    /**
     * Disable form submission for predictive-search-form when Hello Retail is enabled
     */
    function disablePredictiveSearchFormSubmit() {
        // Check if Hello Retail is enabled
        if (!window.themeVariables?.settings?.helloRetailEnable) return;
        
        const initFormDisable = () => {
            const searchForm = document.getElementById('predictive-search-form');
            if (!searchForm) {
                // Retry after a short delay if form not ready
                setTimeout(initFormDisable, 100);
                return;
            }
            
            // Prevent form submission
            searchForm.addEventListener('submit', (e) => {
                e.preventDefault();
                e.stopPropagation();
                return false;
            });
        };
        
        // Start initialization
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', initFormDisable);
        } else {
            setTimeout(initFormDisable, 100);
        }
    }

    // Disable form submission
    disablePredictiveSearchFormSubmit();

    /**
     * Generic function to load recommendations from Hello Retail recoms API
     * @param {string} elementId - The ID of the element to find (e.g., 'hr-recom-alternatives')
     * @param {string} productBoxKey - The product box key from settings
     * @param {string} carouselIdPrefix - Prefix for carousel ID (e.g., 'hr-alternatives-carousel')
     * @param {string} carouselClass - CSS class for carousel (e.g., 'hr-alternatives-carousel')
     * @param {string} titleTextKey - Key for title text in themeVariables.strings
     * @param {string} defaultTitle - Default title if translation not found
     * @param {string} errorMessage - Error message for console.error
     */
    function loadRecommendations(elementId, productBoxKey, carouselIdPrefix, carouselClass, titleTextKey, defaultTitle, errorMessage) {
        // Try multiple selectors to find the element
        let elements = document.querySelectorAll(`#${elementId}[data-product-id]`);
        
        // If not found, try without ID requirement (in case ID is added dynamically)
        if (elements.length === 0) {
            elements = document.querySelectorAll(`[id="${elementId}"][data-product-id]`);
        }
        
        if (elements.length === 0) return;
        
        // Check if Hello Retail is enabled
        if (!window.themeVariables?.settings?.helloRetailEnable) return;
        
        const websiteUuid = window.themeVariables?.settings?.helloRetailWebsiteUuid || '';
        const boxKey = window.themeVariables?.settings?.[productBoxKey] || '';
        
        if (!websiteUuid || !boxKey) {
            return;
        }
        
        elements.forEach(async (element) => {
            const productId = element.getAttribute('data-product-id');
            if (!productId) return;
            
            // Check if already loaded
            if (element.hasAttribute('data-loaded')) return;
            
            try {
                const pdpContext = {};
                const productUrl = (element.getAttribute('data-hr-product-url') || '').trim();
                if (productUrl) {
                    pdpContext.urls = [productUrl];
                } else if (
                    window.themeVariables?.settings?.pageType === 'product' &&
                    typeof window !== 'undefined' &&
                    window.location?.href
                ) {
                    pdpContext.urls = [window.location.href];
                }

                const requestItem = buildManagedRecomRequestItem({
                    key: boxKey,
                    format: 'json',
                    context: pdpContext,
                    productNumbers: productId
                });

                const data = await postHelloRetailRecoms(websiteUuid, [requestItem]);
                
                if (!data || !data.responses || !data.responses[0] || !data.responses[0].products) {
                    return;
                }
                
                const products = data.responses[0].products || [];
                
                if (products.length === 0) {
                    return;
                }
                
                // Generate unique carousel ID
                const carouselId = `${carouselIdPrefix}-${productId}`;
                
                // Get title text
                const titleText = window.themeVariables?.strings?.[titleTextKey] || defaultTitle;
                
                // Render carousel with products
                const carouselHtml = renderRecommendationsCarousel(products, carouselId, carouselClass, titleText);
                element.innerHTML = carouselHtml;
                element.setAttribute('data-loaded', 'true');
                
            } catch (error) {
                console.error(errorMessage, error);
            }
        });
    }

    /**
     * Load Hello Retail recommendation carousels for any element matching `.hr-recom-box`.
     * Each host must define `data-box-key` (Hello Retail box key). Optional `data-product-id`
     * sends product context. Optional `data-title` overrides the carousel heading.
     * Optional context: `data-hr-context` (JSON object), or `data-hr-context-product-numbers` (comma-separated ids),
     * `data-hr-context-urls` (URLs joined with `|` — use the collection page URL for category-scoped Managed boxes),
     * `data-hr-product-url` (single canonical product URL).
     *
     * @param {ParentNode} [root=document] - Root node to search under (defaults to full document).
     */
    function loadHrRecomBoxes(root = document) {
        if (!window.themeVariables?.settings?.helloRetailEnable) return;

        const websiteUuid = window.themeVariables?.settings?.helloRetailWebsiteUuid || '';
        if (!websiteUuid) return;

        const boxes = root.querySelectorAll('.hr-recom-box:not([data-loaded])');
        if (boxes.length === 0) return;

        const defaultTitle =
            window.themeVariables?.strings?.helloRetailTextRecommendations || 'Recommendations';

        boxes.forEach(async (element) => {
            const boxKey = (element.getAttribute('data-box-key') || '').trim();
            if (!boxKey) return;

            if (element.hasAttribute('data-loaded')) return;

            const mergedContext = parseHrRecomContextFromElement(element);
            const legacyPid = (element.getAttribute('data-product-id') || '').trim();
            const pnRaw = mergedContext.productNumbers;
            const pnFromContext =
                typeof pnRaw === 'string'
                    ? pnRaw.trim()
                    : pnRaw != null && String(pnRaw).trim()
                      ? String(pnRaw).trim()
                      : '';
            const pnForRoot = pnFromContext || legacyPid || undefined;

            const requestItem = buildManagedRecomRequestItem({
                key: boxKey,
                format: 'json',
                context: mergedContext,
                productNumbers: pnForRoot
            });

            try {
                const data = await postHelloRetailRecoms(websiteUuid, [requestItem]);

                if (!data || !data.responses || !data.responses[0] || !data.responses[0].products) {
                    return;
                }

                const products = data.responses[0].products || [];
                if (products.length === 0) {
                    return;
                }

                const instanceId =
                    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                        ? crypto.randomUUID().replace(/-/g, '')
                        : `${Date.now()}_${Math.random().toString(16).slice(2)}`;
                const carouselId = `hr-recom-box-carousel-${instanceId}`;

                const titleAttr = (element.getAttribute('data-title') || '').trim();
                const titleText = titleAttr || defaultTitle;

                const carouselHtml = renderRecommendationsCarousel(
                    products,
                    carouselId,
                    'hr-recom-box-carousel',
                    titleText
                );
                element.innerHTML = carouselHtml;
                element.setAttribute('data-loaded', 'true');
            } catch (error) {
                console.error('Error loading Hello Retail recommendation box:', error);
            }
        });
    }
    
    /**
     * Generic function to render recommendations carousel HTML
     * @param {Array} products - Array of product objects
     * @param {string} carouselId - Unique ID for the carousel
     * @param {string} carouselClass - CSS class for the carousel
     * @param {string} titleText - Title text to display
     */
    function renderRecommendationsCarousel(products, carouselId, carouselClass, titleText) {
        // Create a temporary renderer instance to use renderProducts method
        const tempContainer = document.createElement('div');
        const renderer = new HelloRetailSearchRenderer(tempContainer);
        const productsHtml = renderer.renderProducts(products);
        
        return `
            <div class="hr-products-header">${titleText}</div>
            <div class="floating-controls-container">
                <carousel-prev-button class="floating-controls__control floating-controls__control--offset" aria-controls="${carouselId}">
                    <button disabled class="circle-button circle-button--xl">
                        <span class="sr-only">Previous</span>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8.5 10.5L5.5 7.5L8.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </carousel-prev-button>
                
                <scroll-carousel id="${carouselId}" class="${carouselClass} scroll-area bleed" cell-selector=".hr-product-card">
                    ${productsHtml}
                </scroll-carousel>
                
                <carousel-next-button class="floating-controls__control floating-controls__control--offset" aria-controls="${carouselId}">
                    <button class="circle-button circle-button--xl">
                        <span class="sr-only">Next</span>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5.5 4.5L8.5 7.5L5.5 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </carousel-next-button>
            </div>
        `;
    }
    
    // Load alternatives recommendations
    function loadAlternativesRecommendations() {
        loadRecommendations(
            'hr-recom-alternatives',
            'helloRetailAlternativesProductBoxKey',
            'hr-alternatives-carousel',
            'hr-alternatives-carousel',
            'helloRetailTextAlternatives',
            'Alternatives',
            'Error loading alternatives recommendations:'
        );
    }
    
    // Load bought together recommendations
    function loadBoughtTogetherRecommendations() {
        loadRecommendations(
            'hr-recom-bought-together',
            'helloRetailBoughtTogetherProductBoxKey',
            'hr-bought-together-carousel',
            'hr-bought-together-carousel',
            'helloRetailTextBoughtTogether',
            'Bought together',
            'Error loading bought together recommendations:'
        );
    }
    
    // Setup alternatives recommendations loader
    function setupAlternativesLoader() {
        // Load immediately if DOM is ready
        loadAlternativesRecommendations();
        loadBoughtTogetherRecommendations();
        loadHrRecomBoxes();
        
        // Also use MutationObserver to detect dynamically added elements
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === 1) { // Element node
                        if (node.id === 'hr-recom-alternatives' || node.querySelector('#hr-recom-alternatives')) {
                            loadAlternativesRecommendations();
                        }
                        if (node.id === 'hr-recom-bought-together' || node.querySelector('#hr-recom-bought-together')) {
                            loadBoughtTogetherRecommendations();
                        }
                        if (node.matches?.('.hr-recom-box') || node.querySelector?.('.hr-recom-box')) {
                            loadHrRecomBoxes();
                        }
                    }
                });
            });
        });
        
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
        
        // Also try loading after a delay in case element is added dynamically
        setTimeout(() => {
            loadAlternativesRecommendations();
            loadBoughtTogetherRecommendations();
            loadHrRecomBoxes();
        }, 500);
        
        setTimeout(() => {
            loadAlternativesRecommendations();
            loadBoughtTogetherRecommendations();
            loadHrRecomBoxes();
        }, 2000);
    }
    
    // Initialize alternatives loader when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setupAlternativesLoader);
    } else {
        // DOM already loaded
        setTimeout(setupAlternativesLoader, 100);
    }

    /**
     * Setup global size selector event listeners
     * This ensures size selector triggers work for search results, initial content, and recommendations boxes
     */
    function setupGlobalSizeSelectorListeners() {
        // Check if Hello Retail is enabled
        if (!window.themeVariables?.settings?.helloRetailEnable) return;
        
        // Create a temporary renderer instance just to initialize the listeners
        // The listeners are set up on document level, so they work globally
        const tempContainer = document.createElement('div');
        const renderer = new HelloRetailSearchRenderer(tempContainer);
        renderer.initializeSizeSelectorListeners();
    }

    // Initialize global size selector listeners when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setupGlobalSizeSelectorListeners);
    } else {
        // DOM already loaded
        setTimeout(setupGlobalSizeSelectorListeners, 100);
    }

})();
